import React, { useState, useRef, useEffect } from 'react';
import { LuCircleCheck, LuFileSpreadsheet, LuUpload, LuX } from 'react-icons/lu';
import useFeeStore from '../../../store/feeStore';
import Button from '../../../components/common/Button';
import Notice from '../../../components/common/Notice';
import Table, { tdClass } from '../../../components/common/Table';
import { Select } from '../../../components/common/FormControls';
import { useToast } from '../../../components/common/Toast';
import { isValidSpreadsheetFile, isExcelFile, parseCSVContent, parseExcelFile } from '../../../utils/utils';

const FeeUploadWindow = ({ isOpen, onClose, onUploadSuccess, uploadType = 'receipt' }) => {
  const { showToast, ToastComponent } = useToast();
  const fileInputRef = useRef(null);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  
  const { 
    uploadFeeReceipts,
    uploadFeeDues, 
    clearUploadState,
    isUploading,
    uploadError,
    uploadSuccess,
    uploadMessage,
    uploadWarnings
  } = useFeeStore();

  // Handle upload state changes
  useEffect(() => {
    if (uploadSuccess && uploadMessage) {
      if (!uploadWarnings) {
        showToast(uploadMessage, 'success');
        if (onUploadSuccess) {
          onUploadSuccess({ success: true, message: uploadMessage });
        }
        // Reset local state and close
        setSelectedFile(null);
        setCsvData([]);
        setCsvHeaders([]);
        setFileError(null);
        setSelectedMonth('');
        clearUploadState();
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        onClose();
      }
    }
  }, [uploadSuccess, uploadMessage, uploadWarnings, showToast, onUploadSuccess, onClose, clearUploadState]);

  // Reset state when window closes
  const handleClose = () => {
    setSelectedFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setFileError(null);
    setSelectedMonth('');
    clearUploadState();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  // Helper to render formatted upload message highlighting success
  const renderFormattedMessage = () => {
    if (!uploadMessage) return null;

    // Check if the message contains "Uploaded X records successfully"
    const match = uploadMessage.match(/Uploaded (\d+) records successfully\.(.*)/);
    if (match) {
      const [, successCount, rest] = match;
      return (
        <div className="flex flex-col gap-1 mt-1">
          <div className="flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-300">
            <LuCircleCheck aria-hidden className="size-4 shrink-0" />
            <span>Uploaded {successCount} records successfully.</span>
          </div>
          {rest && <span className="text-sm text-ui-ink-2">{rest.trim()}</span>}
        </div>
      );
    }

    return <span className="text-sm text-ui-ink-2">{uploadMessage}</span>;
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    
    if (!file) {
      return;
    }

    // Clear previous errors
    setFileError(null);

    if (!isValidSpreadsheetFile(file)) {
      setFileError('Please select a valid CSV or Excel file (.csv, .xlsx, .xls) — max 10MB');
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();

    if (isExcelFile(file)) {
      // Excel path: read as binary ArrayBuffer → parse with xlsx
      reader.onload = (e) => {
        try {
          const parsedData = parseExcelFile(e.target.result);

          if (parsedData.length === 0) {
            setFileError('Excel file is empty or has no data rows');
            setSelectedFile(null);
            return;
          }

          const headers = Object.keys(parsedData[0]);
          setCsvHeaders(headers);
          setCsvData(parsedData);
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          setFileError('Error reading Excel file. Please check the format.');
          setSelectedFile(null);
        }
      };

      reader.onerror = () => {
        setFileError('Error reading file. Please try again.');
        setSelectedFile(null);
      };

      reader.readAsArrayBuffer(file);
    } else {
      // CSV path: unchanged
      reader.onload = (e) => {
        try {
          const csvContent = e.target.result;
          const parsedData = parseCSVContent(csvContent);

          if (parsedData.length === 0) {
            setFileError('CSV file is empty or has invalid format');
            setSelectedFile(null);
            return;
          }

          // Extract headers from first row
          const headers = Object.keys(parsedData[0]);
          setCsvHeaders(headers);
          setCsvData(parsedData);
        } catch (error) {
          console.error('Error parsing CSV:', error);
          setFileError('Error reading CSV file. Please check the format.');
          setSelectedFile(null);
        }
      };

      reader.onerror = () => {
        setFileError('Error reading file. Please try again.');
        setSelectedFile(null);
      };

      reader.readAsText(file);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile || csvData.length === 0) {
      setFileError('Please select a valid file first');
      return;
    }

    // For due uploads, validate month selection
    if (uploadType === 'due' && !selectedMonth) {
      setFileError('Please select a month for the fee dues');
      return;
    }

    // Clear any previous file errors
    setFileError(null);

    // Convert CSV data back to string format for the store function
    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map(row => csvHeaders.map(header => row[header] || '').join(','))
    ].join('\n');
    
    // Call the appropriate upload function based on type
    if (uploadType === 'due') {
      await uploadFeeDues(csvContent, selectedMonth);
    } else {
      await uploadFeeReceipts(csvContent);
    }
  };

  // Choose CSV button click
  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) {
    return null;
  }

  const resetSelectedFile = () => {
    setSelectedFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setFileError(null);
    setSelectedMonth('');
    clearUploadState();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const recordLabel = uploadType === 'due' ? 'due' : 'receipt';

  const renderBody = () => {
    // Uploaded, but some rows were skipped: show which and why
    if (uploadSuccess && uploadWarnings) {
      return (
        <div className="flex h-full flex-col gap-4 p-6">
          <Notice tone="warning" title="Upload finished, but some rows were skipped">
            {renderFormattedMessage()}
          </Notice>
          <h3 className="text-[11px] font-semibold tracking-wider text-ui-ink-3 uppercase">
            Skipped rows ({uploadWarnings.length})
          </h3>
          <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-ui-line">
            <Table head={['Row', 'Admission no.', 'Reason']}>
              {uploadWarnings.map((warning, warningIndex) => (
                <tr key={warningIndex}>
                  <td className={`${tdClass} w-20 font-medium`}>{warning.rowIndex}</td>
                  <td className={`${tdClass} font-mono text-[13px]`}>{warning.admissionNo || '—'}</td>
                  <td className={`${tdClass} text-red-700 dark:text-red-300`}>{warning.error}</td>
                </tr>
              ))}
            </Table>
          </div>
        </div>
      );
    }

    // No file yet: explain what the file needs and let them pick one
    if (!selectedFile) {
      return (
        <div className="flex h-full items-center justify-center p-8">
          <button
            type="button"
            onClick={handleChooseFile}
            disabled={isUploading}
            className="flex w-full max-w-lg flex-col items-center rounded-xl border-2 border-dashed border-ui-input px-8 py-10 text-center transition-colors hover:border-brand-400 hover:bg-ui-brand-soft"
          >
            <span className="mb-4 flex size-12 items-center justify-center rounded-full bg-ui-brand-soft text-brand-600 dark:text-brand-300">
              <LuFileSpreadsheet aria-hidden className="size-6" />
            </span>
            <span className="text-[15px] font-semibold text-ui-ink">Choose an Excel or CSV file</span>
            <span className="mt-1.5 text-sm text-ui-ink-2">
              {uploadType === 'due'
                ? 'The file needs admission numbers and the amount due for each student.'
                : 'The file needs admission numbers, voucher numbers and the payment details.'}
            </span>
            <span className="mt-3 text-xs text-ui-ink-3">.xlsx, .xls or .csv · up to 10 MB</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      );
    }

    // A file is chosen: show its rows so they can be checked before uploading
    return (
      <div className="flex h-full flex-col">
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-ui-line-soft px-6 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <LuFileSpreadsheet aria-hidden className="size-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-ui-ink">{selectedFile.name}</p>
              <p className="text-xs text-ui-ink-3">
                {csvData.length} rows · {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {uploadType === 'due' && (
              <Select
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(event.target.value)}
                aria-label="Month of these dues"
                className="w-44"
              >
                <option value="">Month of these dues</option>
                {months.map((month) => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </Select>
            )}
            <Button variant="secondary" size="sm" onClick={resetSelectedFile}>
              Choose another file
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <Table head={csvHeaders}>
            {csvData.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {csvHeaders.map((header, columnIndex) => (
                  <td key={columnIndex} className={`${tdClass} whitespace-nowrap`}>
                    {row[header] || '—'}
                  </td>
                ))}
              </tr>
            ))}
          </Table>
        </div>
      </div>
    );
  };

  const showFooter = (selectedFile && !uploadSuccess) || (uploadSuccess && uploadWarnings);

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="fee-upload-title">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />

      <div className="relative flex h-[min(700px,90vh)] w-full max-w-5xl animate-enter flex-col overflow-hidden rounded-xl border border-ui-line bg-ui-card shadow-pop">
        <div className="flex shrink-0 items-center justify-between border-b border-ui-line-soft px-6 py-4">
          <div>
            <h2 id="fee-upload-title" className="text-base font-semibold text-ui-ink">
              {uploadType === 'due' ? 'Upload fee dues' : 'Upload fee receipts'}
            </h2>
            <p className="mt-0.5 text-sm text-ui-ink-2">
              {selectedFile ? 'Check the rows below, then upload.' : 'Add many records at once from a spreadsheet.'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="-mr-2 flex size-8 items-center justify-center rounded-lg text-ui-ink-3 transition-colors hover:bg-ui-hover hover:text-ui-ink"
          >
            <LuX aria-hidden className="size-4" />
          </button>
        </div>

        {(fileError || uploadError) && (
          <div className="max-h-48 shrink-0 overflow-y-auto border-b border-ui-line-soft px-6 py-3">
            <Notice tone="error" title={fileError ? 'There is a problem with the file' : 'The upload failed'}>
              {fileError || uploadError?.message || String(uploadError)}
            </Notice>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto">{renderBody()}</div>

        {showFooter && (
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-ui-line-soft px-6 py-4">
            {uploadSuccess && uploadWarnings ? (
              <Button
                onClick={() => {
                  if (onUploadSuccess) {
                    onUploadSuccess({ success: true, message: uploadMessage });
                  }
                  handleClose();
                }}
              >
                Done
              </Button>
            ) : (
              <>
                {uploadType === 'due' && !selectedMonth && (
                  <span className="text-sm text-ui-ink-3">Choose the month of these dues first.</span>
                )}
                <Button variant="secondary" onClick={handleClose} disabled={isUploading}>
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  loading={isUploading}
                  disabled={!selectedFile || csvData.length === 0 || (uploadType === 'due' && !selectedMonth)}
                  leftIcon={<LuUpload className="size-4" />}
                >
                  {isUploading ? 'Uploading…' : `Upload ${csvData.length} ${recordLabel} records`}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {ToastComponent}
    </div>
  );
};

export default FeeUploadWindow;
